import { useMemo } from 'react'

import { useCronograma } from '@/modules/tablero/useCronograma'
import { bloquesJornada } from '@/shared/utilidades/jornada'

import { partes } from './horario'
import { useVisitasProgramadas } from './useVisitas'
import estilos from './ProgramarVisita.module.css'

type Ocupado = { inicio: Date; fin: Date; que: string; cliente: string | null; visita: boolean }

const hhmm = (d: Date) =>
  new Intl.DateTimeFormat('es-CO', {
    timeZone: 'America/Bogota',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d)

/**
 * LO QUE ESE TÉCNICO YA TIENE ESE DÍA, para programar la visita sin
 * cruzarla: sus trabajos (con los tramos de la jornada) y sus otras visitas.
 * Lo que se cruza con la hora escogida va en rojo. No bloquea —la oficina
 * sabe si una visita de media hora cabe antes de arrancar la obra—, avisa.
 */
export function AgendaDelTecnico({
  colaboradorId,
  nombre,
  fecha,
  inicio,
  fin,
  sinContar,
}: {
  colaboradorId: string
  nombre: string
  fecha: string
  inicio: Date | null
  fin: Date | null
  /** La orden que se está programando: su propia visita no se cruza consigo. */
  sinContar: string
}) {
  const { filas } = useCronograma()
  const { visitas } = useVisitasProgramadas()

  const ocupado = useMemo(() => {
    const lista: Ocupado[] = []
    for (const f of filas) {
      if (f.colaborador_id !== colaboradorId || !f.fecha_inicio_programada) continue
      if (f.estado === 'cerrada' || f.estado === 'anulada') continue
      for (const s of bloquesJornada(
        f.fecha_inicio_programada,
        f.horas_asignadas === null ? null : Number(f.horas_asignadas),
      )) {
        if (s.tipo !== 'trabajo' || partes(s.inicio.toISOString()).fecha !== fecha) continue
        lista.push({
          inicio: s.inicio,
          fin: s.fin,
          que: f.numero,
          cliente: f.cliente_nombre,
          visita: false,
        })
      }
    }
    for (const v of visitas) {
      if (v.colaborador_id !== colaboradorId || v.orden_servicio_id === sinContar) continue
      if (partes(v.visita_inicio).fecha !== fecha) continue
      const i = new Date(v.visita_inicio)
      lista.push({
        inicio: i,
        fin: new Date(i.getTime() + (v.visita_horas ?? 1) * 3_600_000),
        que: `Visita ${v.numero}`,
        cliente: v.cliente,
        visita: true,
      })
    }
    return lista.sort((a, b) => a.inicio.getTime() - b.inicio.getTime())
  }, [filas, visitas, colaboradorId, fecha, sinContar])

  const primerNombre = nombre.split(' ')[0]

  if (ocupado.length === 0) {
    return (
      <p className={estilos.agendaLibre}>Ese día {primerNombre} no tiene nada más: está libre.</p>
    )
  }

  const cruces = ocupado.filter((o) => inicio && fin && o.inicio < fin && o.fin > inicio)

  return (
    <div className={estilos.agenda}>
      <p className={estilos.agendaTitulo}>
        Ese día {primerNombre} tiene
        {cruces.length > 0 && (
          <b className={estilos.agendaCruce}>
            {' '}
            · se cruza con {cruces.length === 1 ? 'uno' : cruces.length}
          </b>
        )}
      </p>
      <ul>
        {ocupado.map((o, i) => {
          const cruza = cruces.includes(o)
          return (
            <li key={i} className={cruza ? estilos.cruza : undefined}>
              <span className={estilos.agendaHora}>
                {hhmm(o.inicio)}–{hhmm(o.fin)}
              </span>
              <span>
                <b>{o.que}</b>
                {o.cliente && ` · ${o.cliente}`}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
